import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import TarefaDetalhesModal from '../components/TarefaDetalhesModal';
import { FiPlus, FiCheckSquare, FiBriefcase, FiFileText, FiCalendar, FiAlertCircle } from 'react-icons/fi';
import './TarefasKanban.css';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR');
};

const getStatusBadge = (status) => {
  const badges = {
    pendente: 'badge-secondary',
    em_andamento: 'badge-info',
    done_pending_approval: 'badge-warning',
    concluida: 'badge-success',
    atrasada: 'badge-danger',
    cancelada: 'badge-secondary'
  };
  return badges[status] || 'badge-secondary';
};

const getStatusLabel = (status) => {
  const labels = {
    pendente: 'Pendente',
    em_andamento: 'Em Andamento',
    done_pending_approval: 'Aguardando Aprovação',
    concluida: 'Concluída',
    atrasada: 'Atrasada',
    cancelada: 'Cancelada'
  };
  return labels[status] || status;
};

const getStatusDateClass = (status) => {
  const classes = {
    atrasada: 'kanban-card-vencimento-atrasada',
    pendente: 'kanban-card-vencimento-pendente',
    em_andamento: 'kanban-card-vencimento-em-andamento',
    done_pending_approval: 'kanban-card-vencimento-aguardando',
    concluida: 'kanban-card-vencimento-concluida',
    cancelada: 'kanban-card-vencimento-cancelada'
  };
  return classes[status] || 'kanban-card-vencimento-pendente';
};

const truncar = (str, maxLen) => (str && str.length > maxLen ? `${str.substring(0, maxLen)}...` : str || '');

const isTarefaAtrasada = (t) => (t.status_efetivo || t.status) === 'atrasada';

/** Coluna "Atrasados" só mostra tarefas atrasadas que NÃO estão em andamento, aguardando aprovação ou concluídas */
const STATUS_QUE_FICAM_NA_COLUNA = {
  pendente: (t) => (t.status === 'pendente' || t.status === 'atrasada') && !isTarefaAtrasada(t),
  atrasada: (t) => isTarefaAtrasada(t) && !['em_andamento', 'done_pending_approval', 'concluida', 'cancelada'].includes(t.status || ''),
  em_andamento: (t) => t.status === 'em_andamento',
  aguardando: (t) => t.status === 'done_pending_approval',
  concluida: (t) => t.status === 'concluida'
};

const COLUNAS = [
  { id: 'pendente', titulo: 'Pendente', statuses: ['pendente'] },
  { id: 'atrasada', titulo: 'Atrasados', statuses: ['atrasada'] },
  { id: 'em_andamento', titulo: 'Em andamento', statuses: ['em_andamento'] },
  { id: 'aguardando', titulo: 'Aguardando aprovação', statuses: ['done_pending_approval'] },
  { id: 'concluida', titulo: 'Concluída', statuses: ['concluida'] }
];

const TarefasKanban = () => {
  const { user, hasPermission } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tarefas, setTarefas] = useState([]);
  const [licencas, setLicencas] = useState([]);
  const [processos, setProcessos] = useState([]);
  const [condicionantes, setCondicionantes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [filterLicenca, setFilterLicenca] = useState('');
  const [filterProcesso, setFilterProcesso] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetalhesModal, setShowDetalhesModal] = useState(false);
  const [selectedTarefa, setSelectedTarefa] = useState(null);
  const [documentos, setDocumentos] = useState([]);
  const [comentarios, setComentarios] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [responsaveis, setResponsaveis] = useState([]);
  const [novoComentario, setNovoComentario] = useState('');
  const [showAddResponsavelForm, setShowAddResponsavelForm] = useState(false);
  const [addResponsavelId, setAddResponsavelId] = useState('');
  const [openBadgeDropdown, setOpenBadgeDropdown] = useState(null);
  const [dragOverColunaId, setDragOverColunaId] = useState(null);
  const [draggingTarefaId, setDraggingTarefaId] = useState(null);
  const justDroppedRef = useRef(false);
  const [formData, setFormData] = useState({
    condicionante_id: '',
    usuario_responsavel_id: '',
    titulo: '',
    descricao: '',
    data_vencimento: '',
    prioridade: 'media'
  });

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterLicenca) params.set('licenca_id', filterLicenca);
      if (filterProcesso) params.set('processo_licenciamento_id', filterProcesso);
      const tarefasUrl = params.size ? `/api/tarefas?${params}` : '/api/tarefas';
      const tarefasRes = await api.get(tarefasUrl);
      const tarefasData = tarefasRes.data;
      setTarefas(Array.isArray(tarefasData) ? tarefasData : (tarefasData?.data || []));

      const licencasRes = await api.get('/api/licencas').catch(() => ({ data: [] }));
      const licencasList = Array.isArray(licencasRes.data) ? licencasRes.data : (licencasRes.data?.data || []);
      setLicencas(licencasList);

      const processosRes = await api.get('/api/processos-licenciamento').catch(() => ({ data: [] }));
      setProcessos(Array.isArray(processosRes.data) ? processosRes.data : (processosRes.data?.data || []));

      let conds = [];
      try {
        const condRes = await api.get('/api/condicionantes');
        conds = condRes.data || [];
      } catch {
        const licencasForCond = await api.get('/api/licencas');
        const licArr = Array.isArray(licencasForCond.data) ? licencasForCond.data : (licencasForCond.data?.data || []);
        const condPromises = licArr.map((lic) =>
          api.get(`/api/condicionantes?licenca_id=${lic.id}`).catch(() => ({ data: [] }))
        );
        const condRes = await Promise.all(condPromises);
        conds = condRes.flatMap((r) => r.data || []);
      }
      setCondicionantes(conds);

      const empRes = await api.get('/api/empreendimentos');
      const usersPromises = (empRes.data || []).map((emp) =>
        api.get(`/api/usuarios/empreendimento/${emp.id}`).catch(() => ({ data: [] }))
      );
      const usersRes = await Promise.all(usersPromises);
      setUsuarios(usersRes.flatMap((r) => r.data || []));
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setTarefas([]);
    } finally {
      setLoading(false);
    }
  }, [filterLicenca, filterProcesso]);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  const tarefaIdFromUrl = searchParams.get('tarefa');
  useEffect(() => {
    if (!tarefaIdFromUrl) return;
    api.get(`/api/tarefas/${tarefaIdFromUrl}`)
      .then((res) => {
        if (res.data) {
          setSelectedTarefa(res.data);
          setShowDetalhesModal(true);
          setNovoComentario('');
          setShowAddResponsavelForm(false);
          setAddResponsavelId('');
          carregarDetalhesTarefa(res.data.id);
        }
        setSearchParams({}, { replace: true });
      })
      .catch(() => setSearchParams({}, { replace: true }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tarefaIdFromUrl]);

  const carregarDetalhesTarefa = useCallback(async (tarefaId) => {
    if (!tarefaId) return;
    try {
      const [docsRes, comRes, histRes, respRes] = await Promise.all([
        api.get(`/api/tarefas/${tarefaId}/documentos`),
        api.get(`/api/tarefas/${tarefaId}/comentarios`).catch(() => ({ data: [] })),
        api.get(`/api/tarefas/${tarefaId}/historico-completo`).catch(() => ({ data: [] })),
        api.get(`/api/tarefas/${tarefaId}/responsaveis`).catch(() => ({ data: [] }))
      ]);
      const docData = docsRes.data;
      setDocumentos(Array.isArray(docData) ? docData : (docData?.data ?? []));
      setComentarios(comRes.data || []);
      setHistorico(histRes.data || []);
      setResponsaveis(respRes.data || []);
    } catch (err) {
      console.error('Erro ao carregar detalhes:', err);
    }
  }, []);

  const abrirDetalhes = (tarefa) => {
    setSelectedTarefa(tarefa);
    setShowDetalhesModal(true);
    setNovoComentario('');
    setShowAddResponsavelForm(false);
    setAddResponsavelId('');
    carregarDetalhesTarefa(tarefa.id);
  };

  const fecharDetalhes = () => {
    setShowDetalhesModal(false);
    setSelectedTarefa(null);
    setDocumentos([]);
    setComentarios([]);
    setHistorico([]);
    setResponsaveis([]);
    setShowAddResponsavelForm(false);
    setOpenBadgeDropdown(null);
    carregarDados();
  };

  const handleMudarStatus = async (tarefaId, novoStatus) => {
    try {
      await api.put(`/api/tarefas/${tarefaId}`, { status: novoStatus });
      setSelectedTarefa((prev) => (prev?.id === tarefaId ? { ...prev, status: novoStatus } : prev));
      setOpenBadgeDropdown(null);
      carregarDados();
      carregarDetalhesTarefa(tarefaId);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao atualizar status');
    }
  };

  const handleMudarPrioridade = async (tarefaId, novaPrioridade) => {
    try {
      await api.put(`/api/tarefas/${tarefaId}`, { prioridade: novaPrioridade });
      setSelectedTarefa((prev) => (prev?.id === tarefaId ? { ...prev, prioridade: novaPrioridade } : prev));
      setOpenBadgeDropdown(null);
      carregarDados();
      carregarDetalhesTarefa(tarefaId);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao atualizar prioridade');
    }
  };

  const handleMudarVencimento = async (tarefaId, novaData) => {
    try {
      await api.put(`/api/tarefas/${tarefaId}`, { data_vencimento: novaData || null });
      setSelectedTarefa((prev) => (prev?.id === tarefaId ? { ...prev, data_vencimento: novaData } : prev));
      carregarDados();
      carregarDetalhesTarefa(tarefaId);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao atualizar vencimento');
    }
  };

  const handleAdicionarComentario = async (e) => {
    e.preventDefault();
    if (!novoComentario?.trim() || !selectedTarefa) return;
    try {
      await api.post(`/api/tarefas/${selectedTarefa.id}/comentarios`, { conteudo: novoComentario.trim() });
      setNovoComentario('');
      carregarDetalhesTarefa(selectedTarefa.id);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao adicionar comentário');
    }
  };

  const handleAdicionarResponsavel = async (e) => {
    e.preventDefault();
    if (!selectedTarefa || !addResponsavelId) return;
    try {
      await api.post(`/api/tarefas/${selectedTarefa.id}/responsaveis`, { usuario_id: addResponsavelId });
      setResponsaveis((prev) => [...prev, { usuario_id: addResponsavelId, usuario_nome: usuarios.find((u) => u.id === addResponsavelId)?.nome }]);
      setShowAddResponsavelForm(false);
      setAddResponsavelId('');
      carregarDados();
      carregarDetalhesTarefa(selectedTarefa.id);
      alert('Responsável adicionado!');
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao adicionar responsável');
    }
  };

  const handleRemoverResponsavel = async (usuarioId) => {
    if (!selectedTarefa || !usuarioId) return;
    if (!window.confirm('Remover este responsável da tarefa?')) return;
    try {
      await api.delete(`/api/tarefas/${selectedTarefa.id}/responsaveis/${usuarioId}`);
      setResponsaveis((prev) => prev.filter((r) => r.usuario_id !== usuarioId));
      carregarDados();
      carregarDetalhesTarefa(selectedTarefa.id);
      alert('Responsável removido.');
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao remover responsável');
    }
  };

  const handleExcluirAndamento = async (comentarioId) => {
    if (!selectedTarefa || !window.confirm('Excluir este andamento?')) return;
    try {
      await api.delete(`/api/tarefas/${selectedTarefa.id}/comentarios/${comentarioId}`);
      setComentarios((prev) => prev.filter((c) => c.id !== comentarioId));
      carregarDetalhesTarefa(selectedTarefa.id);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao excluir andamento');
    }
  };

  const handleAprovar = async (t) => {
    try {
      await api.post(`/api/tarefas/${t.id}/aprovar`);
      fecharDetalhes();
      carregarDados();
      alert('Tarefa aprovada!');
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao aprovar tarefa');
    }
  };

  const handleSubmitNovaTarefa = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/tarefas', formData);
      setShowModal(false);
      setFormData({ condicionante_id: '', usuario_responsavel_id: '', titulo: '', descricao: '', data_vencimento: '', prioridade: 'media' });
      carregarDados();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao criar tarefa');
    }
  };

  const getTarefasPorColuna = (coluna) => {
    const fn = STATUS_QUE_FICAM_NA_COLUNA[coluna.id];
    const list = fn ? tarefas.filter(fn) : tarefas.filter((t) => coluna.statuses.includes(t.status_efetivo || t.status || 'pendente'));
    if (coluna.id === 'em_andamento') {
      return [...list].sort((a, b) => (isTarefaAtrasada(b) ? 1 : 0) - (isTarefaAtrasada(a) ? 1 : 0));
    }
    return list;
  };

  const handleCardDragStart = (e, tarefa) => {
    e.dataTransfer.setData('text/plain', tarefa.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify({ tarefaId: tarefa.id, statusAtual: tarefa.status_efetivo || tarefa.status }));
    setDraggingTarefaId(tarefa.id);
  };

  const handleCardDragEnd = () => {
    setDraggingTarefaId(null);
    setDragOverColunaId(null);
  };

  const handleColunaDragOver = (e, colunaId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColunaId(colunaId);
  };

  const handleColunaDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverColunaId(null);
    }
  };

  const handleColunaDrop = (e, coluna) => {
    e.preventDefault();
    setDragOverColunaId(null);
    setDraggingTarefaId(null);
    const tarefaId = e.dataTransfer.getData('text/plain');
    const novoStatus = coluna.statuses[0];
    if (!tarefaId || !novoStatus) return;
    const tarefa = tarefas.find((t) => t.id === tarefaId);
    if (!tarefa) return;
    const statusAtual = tarefa.status_efetivo || tarefa.status;
    if (statusAtual === novoStatus) return;
    justDroppedRef.current = true;
    handleMudarStatus(tarefaId, novoStatus);
    setTimeout(() => { justDroppedRef.current = false; }, 150);
  };

  const handleCardClick = (tarefa) => {
    if (justDroppedRef.current) return;
    abrirDetalhes(tarefa);
  };

  if (loading) {
    return (
      <div className="tarefas-kanban-page">
        <div className="loading">Carregando tarefas...</div>
      </div>
    );
  }

  return (
    <div className="tarefas-kanban-page">
      <div className="page-header">
        <h1 className="page-title">Tarefas (Kanban)</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <select
            value={filterLicenca}
            onChange={(e) => {
              setFilterLicenca(e.target.value);
              if (e.target.value) setFilterProcesso('');
            }}
            className="kanban-filter-licenca"
          >
            <option value="">Todas as licenças</option>
            {licencas.map((lic) => (
              <option key={lic.id} value={lic.id}>
                {[lic.tipo, lic.numero].filter(Boolean).join(' ') || lic.empreendimento_nome || `Licença ${lic.id}`}
              </option>
            ))}
          </select>
          <select
            value={filterProcesso}
            onChange={(e) => {
              setFilterProcesso(e.target.value);
              if (e.target.value) setFilterLicenca('');
            }}
            className="kanban-filter-licenca"
          >
            <option value="">Todos os processos de licenciamento</option>
            {processos.map((processo) => (
              <option key={processo.id} value={processo.id}>
                {processo.titulo}{processo.numero_processo ? ` - ${processo.numero_processo}` : ''}
              </option>
            ))}
          </select>
          <Link to="/tarefas" className="btn btn-outline" style={{ textDecoration: 'none' }}>
            Ver em lista
          </Link>
          {hasPermission('task', 'create') && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <FiPlus /> Nova Tarefa
          </button>
          )}
        </div>
      </div>

      <div className="kanban-board">
        {COLUNAS.map((coluna) => (
          <div
            key={coluna.id}
            className={`kanban-coluna ${dragOverColunaId === coluna.id ? 'kanban-coluna-drag-over' : ''}`}
          >
            <div className="kanban-coluna-header">
              <h3>{coluna.titulo}</h3>
              <span className="kanban-badge">{getTarefasPorColuna(coluna).length}</span>
            </div>
            <div
              className="kanban-coluna-cards"
              onDragOver={(e) => handleColunaDragOver(e, coluna.id)}
              onDragLeave={handleColunaDragLeave}
              onDrop={(e) => handleColunaDrop(e, coluna)}
            >
              {getTarefasPorColuna(coluna).map((tarefa) => (
                <div
                  key={tarefa.id}
                  className={`kanban-card ${draggingTarefaId === tarefa.id ? 'kanban-card-dragging' : ''} ${isTarefaAtrasada(tarefa) ? 'kanban-card-tem-atraso' : ''}`}
                  draggable
                  onDragStart={(e) => handleCardDragStart(e, tarefa)}
                  onDragEnd={handleCardDragEnd}
                  onClick={() => handleCardClick(tarefa)}
                >
                  {isTarefaAtrasada(tarefa) && (
                    <div className="kanban-card-aviso-atraso" role="alert">
                      <FiAlertCircle className="kanban-card-aviso-atraso-icon" />
                      <span>Atrasada</span>
                      {tarefa.data_vencimento && (
                        <span className="kanban-card-aviso-atraso-data">Vencimento: {formatDate(tarefa.data_vencimento)}</span>
                      )}
                    </div>
                  )}
                  <div className="kanban-card-titulo">
                    <FiCheckSquare className="kanban-card-icon" />
                    {tarefa.titulo}
                  </div>
                  {tarefa.empreendimento_nome && (
                    <div className="kanban-card-line kanban-card-empreendimento">
                      <FiBriefcase className="kanban-card-icon" />
                      {tarefa.empreendimento_nome}
                    </div>
                  )}
                  {(tarefa.licenca_tipo || tarefa.licenca_numero) && (
                    <div className="kanban-card-line kanban-card-licenca">
                      <FiFileText className="kanban-card-icon" />
                      {[tarefa.licenca_tipo, tarefa.licenca_numero].filter(Boolean).join(' ')}
                    </div>
                  )}
                  {tarefa.processo_licenciamento_id && (
                    <div className="kanban-card-line kanban-card-licenca">
                      <FiFileText className="kanban-card-icon" />
                      Processo: {tarefa.processo_licenciamento_titulo}
                    </div>
                  )}
                  {tarefa.condicionante_descricao && (
                    <div className="kanban-card-line kanban-card-condicionante" title={tarefa.condicionante_descricao}>
                      {tarefa.condicionante_descricao.length > 50 ? `${tarefa.condicionante_descricao.substring(0, 50)}...` : tarefa.condicionante_descricao}
                    </div>
                  )}
                  <div className="kanban-card-vencimento-destaque">
                    <FiCalendar className="kanban-card-vencimento-icon" />
                    <span className={`kanban-card-vencimento ${getStatusDateClass(tarefa.status_efetivo || tarefa.status)}`}>
                      {formatDate(tarefa.data_vencimento)}
                    </span>
                  </div>
                  <div className="kanban-card-footer">
                    <div className="kanban-card-responsaveis">
                      {(() => {
                        const nomes = (tarefa.responsaveis_nomes || tarefa.responsavel_nome || '').split(',').map((n) => n.trim()).filter(Boolean);
                        return nomes.length > 0 ? (
                          nomes.map((nome, i) => (
                            <div key={`${nome}-${i}`} className="membro-avatar membro-avatar-sm" title={nome}>
                              {nome.charAt(0).toUpperCase()}
                            </div>
                          ))
                        ) : (
                          <span className="membro-empty">-</span>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="kanban-card-badges">
                    <span className={`badge badge-${tarefa.prioridade === 'alta' ? 'danger' : tarefa.prioridade === 'media' ? 'warning' : 'secondary'}`}>
                      {tarefa.prioridade === 'alta' ? 'Alta' : tarefa.prioridade === 'media' ? 'Média' : 'Baixa'}
                    </span>
                    <span className={`badge ${getStatusBadge(tarefa.status_efetivo || tarefa.status)}`}>
                      {getStatusLabel(tarefa.status_efetivo || tarefa.status)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {showDetalhesModal && selectedTarefa && (
        <TarefaDetalhesModal
          tarefa={selectedTarefa}
          onClose={fecharDetalhes}
          documentos={documentos}
          comentarios={comentarios}
          historico={historico}
          responsaveis={responsaveis}
          usuarios={usuarios}
          novoComentario={novoComentario}
          setNovoComentario={setNovoComentario}
          showAddResponsavelForm={showAddResponsavelForm}
          setShowAddResponsavelForm={setShowAddResponsavelForm}
          addResponsavelId={addResponsavelId}
          setAddResponsavelId={setAddResponsavelId}
          openBadgeDropdown={openBadgeDropdown}
          setOpenBadgeDropdown={setOpenBadgeDropdown}
          onAdicionarResponsavel={handleAdicionarResponsavel}
          onRemoverResponsavel={handleRemoverResponsavel}
          onExcluirAndamento={handleExcluirAndamento}
          onAprovar={handleAprovar}
          onMudarStatus={handleMudarStatus}
          onMudarPrioridade={handleMudarPrioridade}
          onMudarVencimento={handleMudarVencimento}
          onAdicionarComentario={handleAdicionarComentario}
          onRefreshDetalhes={carregarDetalhesTarefa}
          onDocumentAdded={(doc) => setDocumentos((prev) => [...(prev || []), doc])}
          hasPermission={hasPermission}
          user={user}
        />
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Nova Tarefa</h2>
            <form onSubmit={handleSubmitNovaTarefa}>
              <div className="form-group">
                <label>Condicionante *</label>
                <select
                  required
                  value={formData.condicionante_id}
                  onChange={(e) => setFormData({ ...formData, condicionante_id: e.target.value })}
                >
                  <option value="">Selecione...</option>
                  {condicionantes.map((cond) => (
                    <option key={cond.id} value={cond.id}>{truncar(cond.descricao, 80)}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Responsável</label>
                <select
                  value={formData.usuario_responsavel_id}
                  onChange={(e) => setFormData({ ...formData, usuario_responsavel_id: e.target.value })}
                >
                  <option value="">Sem responsável</option>
                  {usuarios.map((u) => (
                    <option key={u.id} value={u.id}>{u.nome}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Título *</label>
                <input
                  required
                  value={formData.titulo}
                  onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Descrição</label>
                <textarea
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Vencimento</label>
                  <input
                    type="date"
                    value={formData.data_vencimento}
                    onChange={(e) => setFormData({ ...formData, data_vencimento: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Prioridade</label>
                  <select
                    value={formData.prioridade}
                    onChange={(e) => setFormData({ ...formData, prioridade: e.target.value })}
                  >
                    <option value="baixa">Baixa</option>
                    <option value="media">Média</option>
                    <option value="alta">Alta</option>
                  </select>
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Criar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TarefasKanban;
